import type {
	SortOption,
	IWishListFilter
} from "~/types/registry";
import type {
	IWishListItem
} from "~/types/supabase";

export const useRegistryUtils = () => {
	const sortOptions = ref<SortOption[]>( [
		{
			displayLabel: "Price: Low to High",
			sortValue: "price",
			sortDirection: "asc"
		},
		{
			displayLabel: "Price: High to Low",
			sortValue: "price",
			sortDirection: "desc"
		}
	] );
	const wishListFilterOptions = ref<IWishListFilter[]>( [
		{
			category: "Price",
			options: [ "Under $25", "$25 - $50", "$50 - $100" ]
		},
		{
			category: "Store",
			options: [ "Amazon", "Crate & Barrel" ]
		}
	] );

	const sortWishListItems = ( sortSelection: SortOption, wishListItems: IWishListItem[] ): IWishListItem[] => {
		if ( !sortSelection ) {
			return wishListItems;
		}

		switch ( sortSelection.sortValue ) {
			case "price":
				return wishListItems.sort( ( a: IWishListItem, b: IWishListItem ) => {
					if ( sortSelection.sortDirection === "asc" ) {
						return a.price - b.price;
					} else {
						return b.price - a.price;
					}
				} );

			case "popularity":
				return wishListItems.sort( ( a: IWishListItem, b: IWishListItem ) => {
					if ( sortSelection.sortDirection === "asc" ) {
						return a.quantityNeeded - b.quantityNeeded;
					} else {
						return b.quantityNeeded - a.quantityNeeded;
					}
				} );
			default:
				return wishListItems;
		}
	};

	/**
	 * Parses a price filter option label (eg. "Under $25", "$25 - $50", "Over $100") into a min / max bound.
	 * @param {string} option
	 * @return {{ min?: number, max?: number }}
	 */
	const parsePriceFilterOption = ( option: string ): { min?: number; max?: number } => {
		const bounds = ( option.replace( ",", "" ).match( /\d+(\.\d+)?/g ) ?? [] ).map( Number );
		const normalizedOption = option.toLowerCase();

		if ( normalizedOption.includes( "under" ) || normalizedOption.includes( "below" ) || normalizedOption.includes( "less than" ) ) {
			return { max: bounds[0] };
		}
		if ( normalizedOption.includes( "over" ) || normalizedOption.includes( "above" ) || normalizedOption.includes( "more than" ) || normalizedOption.includes( "0+" ) ) {
			console.log( bounds );
			return { min: bounds[0] };
		}
		if ( bounds.length >= 2 ) {
			return { min: bounds[0], max: bounds[1] };
		}
		if ( bounds.length === 1 ) {
			return { min: bounds[0], max: bounds[0] };
		}
		return {};
	};

	const filterWishListItems = ( filterSelection: IWishListFilter[], wishListItems: IWishListItem[] ): IWishListItem[] => {
		return wishListItems.filter( ( item: IWishListItem ) => {
			return filterSelection.every( ( filter: IWishListFilter ) => {
				if ( filter.options.length === 0 ) {
					// If no options are selected for a category, include all items
					return true;
				}

				switch ( filter.category ) {
					case "Price":
						// Check if the item's price falls within the selected price ranges
						return filter.options.some( ( option: string ) => {
							const { min, max } = parsePriceFilterOption( option );
							if ( min === undefined && max === undefined ) return false;
							if ( min !== undefined && item.price < min ) return false;
							if ( max !== undefined && item.price > max ) return false;
							return true;
						} );

					case "Store":
						// Check if the item's store matches the selected stores
						return filter.options.includes( item.providerName );

					default:
						return true;
				}
			} );
		} );
	};

	return {
		sortOptions,
		wishListFilterOptions,
		sortWishListItems,
		filterWishListItems
	};
};